import { useCallback, useEffect, useReducer } from "react";

import { getDefaultClientDeliveryMessage } from "@/features/shared/utils/getDefaultClientDeliveryMessage";

import { useResolveWhitelabelbyRequestId } from "./useResolveWhiteLabelByRequestId";

interface ModalInteractionsState {
  currentStep: 1 | 2;
  isDiscardDialogOpen: boolean;
  isModalOpen: boolean;
  isSendToChat: boolean;
  mentionedUserIds: number[];
  message: string | undefined;
}

type ModalInteractionsAction =
  | {
      payload: {
        mentionedUserIds: number[];
        message: string | undefined;
      };
      type: "SET_MESSAGE";
    }
  | { payload: string; type: "INITIALIZE_DEFAULT_MESSAGE" }
  | { type: "CLOSE_DISCARD_DIALOG" }
  | { type: "CLOSE_MODAL" }
  | { type: "NEXT_STEP" }
  | { type: "OPEN_DISCARD_DIALOG" }
  | { type: "OPEN_MODAL" }
  | { type: "PREVIOUS_STEP" }
  | { type: "RESET_FOR_DISCARD" }
  | { type: "TOGGLE_SEND_TO_CHAT" };

const initialState: ModalInteractionsState = {
  currentStep: 1,
  isDiscardDialogOpen: false,
  isModalOpen: false,
  isSendToChat: true,
  mentionedUserIds: [],
  message: undefined,
};

function modalInteractionsReducer(
  state: ModalInteractionsState,
  action: ModalInteractionsAction,
  domain?: null | string,
): ModalInteractionsState {
  switch (action.type) {
    case "INITIALIZE_DEFAULT_MESSAGE": {
      return { ...state, message: action.payload };
    }
    case "NEXT_STEP": {
      return { ...state, currentStep: 2 };
    }
    case "PREVIOUS_STEP": {
      return { ...state, currentStep: 1 };
    }
    case "OPEN_MODAL": {
      return {
        ...state,
        isModalOpen: true,
        message: state.message || getDefaultClientDeliveryMessage(domain),
      };
    }
    case "CLOSE_MODAL": {
      return {
        ...initialState,
        isModalOpen: false,
        message: getDefaultClientDeliveryMessage(domain),
      };
    }
    case "OPEN_DISCARD_DIALOG": {
      return { ...state, isDiscardDialogOpen: true };
    }
    case "CLOSE_DISCARD_DIALOG": {
      return { ...state, isDiscardDialogOpen: false };
    }
    case "TOGGLE_SEND_TO_CHAT": {
      return { ...state, isSendToChat: !state.isSendToChat };
    }
    case "SET_MESSAGE": {
      return {
        ...state,
        mentionedUserIds: action.payload.mentionedUserIds,
        message: action.payload.message,
      };
    }
    case "RESET_FOR_DISCARD": {
      return { ...initialState, message: undefined };
    }
    default: {
      return state;
    }
  }
}

interface UseModalInteractionsProps {
  hasFiles: boolean;
  isUploading: boolean;
  onDiscardConfirmed: () => void; // Callback when discard is confirmed
  requestId: number | undefined;
}

export function useModalInteractions({
  hasFiles,
  isUploading,
  onDiscardConfirmed,
  requestId,
}: UseModalInteractionsProps) {
  const { data } = useResolveWhitelabelbyRequestId(requestId);
  const domain = data?.domain;

  const reducer = useCallback(
    (state: ModalInteractionsState, action: ModalInteractionsAction) =>
      modalInteractionsReducer(state, action, domain),
    [domain],
  );

  const [state, dispatch] = useReducer(reducer, initialState);

  useEffect(() => {
    if (typeof window !== "undefined") {
      dispatch({
        payload: getDefaultClientDeliveryMessage(domain),
        type: "INITIALIZE_DEFAULT_MESSAGE",
      });
    }
  }, [domain, data]);

  const handleNextPress = useCallback(() => {
    dispatch({ type: "NEXT_STEP" });
  }, []);

  const handleBackPress = useCallback(() => {
    dispatch({ type: "PREVIOUS_STEP" });
  }, []);

  const handleCancelPress = useCallback(() => {
    if (hasFiles || isUploading) {
      dispatch({ type: "OPEN_DISCARD_DIALOG" });
    } else {
      dispatch({ type: "CLOSE_MODAL" });
    }
  }, [hasFiles, isUploading]);

  const handleOpenChange = useCallback(
    (isOpen: boolean) => {
      if (isOpen) {
        dispatch({ type: "OPEN_MODAL" });
      } else {
        handleCancelPress();
      }
    },
    [handleCancelPress],
  );

  const handleDiscardDismiss = useCallback(() => {
    dispatch({ type: "CLOSE_DISCARD_DIALOG" });
  }, []);

  const handleDiscardConfirm = useCallback(() => {
    onDiscardConfirmed();
    dispatch({ type: "RESET_FOR_DISCARD" });
  }, [onDiscardConfirmed]);

  const handleSetMessage = useCallback(
    (payload: { mentionedUserIds: number[]; message: string | undefined }) => {
      dispatch({ payload, type: "SET_MESSAGE" });
    },
    [],
  );

  const handleToggleSendToChat = useCallback(() => {
    dispatch({ type: "TOGGLE_SEND_TO_CHAT" });
  }, []);

  const openModal = useCallback(() => {
    dispatch({ type: "OPEN_MODAL" });
  }, []);

  return {
    ...state,
    handleBackPress,
    handleCancelPress,
    handleDiscardConfirm,
    handleDiscardDismiss,
    handleNextPress,
    handleOpenChange,
    handleSetMessage,
    handleToggleSendToChat,
    openModal,
  };
}
